import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, Collapse, Drawer, Row, Table,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import {
  getSageData, getSageTimeSync, resolveSageTime, writeSageEquipment, writeSageTime,
} from '../../state/settings.actions';
import { getIdMap, safeExtractFromObject } from '../../../helpers/helpers';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import SearchFormFilterHeader from '../../../forms/CompletedForms/SearchFormFilterHeader';
import SageTimeSyncTaskDetails from './SageTimeSyncTaskDetails';
import useUpdateCostcodeIdMap from '../../../costcodes/useUpdateCostcodeIdMap';
import {
  EQUIPMENT_FILTERS,
  formatEquipmentSyncSummary,
  getEquipmentSyncState,
  getRetryableEquipmentIds,
  getUniqueFilters,
  isTimeEntrySynced,
  matchesEquipmentFilter,
} from './sageTimeSync.helpers';

const { Panel } = Collapse;
const DEFAULT_PANELS = ['unresolved', 'inProgress', 'resolved'];
const TASK_TABLE_SCROLL = { x: 'max-content' };

const sortByLookupName = (idKey, nameMap) => (a, b) => {
  const aName = (nameMap[a?.[idKey]]?.name || '~').toLowerCase().trim();
  const bName = (nameMap[b?.[idKey]]?.name || '~').toLowerCase().trim();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return 0;
};

const seconds = (ms) => ms * 1000;
const minutes = (s) => seconds(s) * 60;

const writtenThreshold = minutes(15) * 4;
export default function SageTimeSyncDrawer({
  visible,
  onClose,
  selectedIntegration,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    sageTime: {
      [selectedIntegration?.id]: sageTimeMap = {},
    } = {},
  } = useSelector((state) => state.settings);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const users = useSelector((state) => state.users.users);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const projects = useSelector((state) => state.projects.projects);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const costcodeTasks = useMemo(() => Object.values(sageTimeMap), [sageTimeMap]);
  useUpdateCostcodeIdMap({ items: costcodeTasks, columns: ['name', 'sageId'] });
  const costcodeMap = useSelector((state) => state.costcodes.costcodeIdMap);

  const [selected, setSelected] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState();
  const [loading, setLoading] = useState();
  const [retryingEquipmentId, setRetryingEquipmentId] = useState();
  const [activePanels, setActivePanels] = useState(DEFAULT_PANELS);

  const selectedTask = selectedTaskId ? sageTimeMap[selectedTaskId] : undefined;

  const {
    unresolvedTasks,
    inProgressTasks,
    resolvedTasks,
  } = useMemo(() => {
    const unresolved = [];
    const inProgress = [];
    const resolved = [];
    const now = DateTime.local().toMillis();
    Object.values(sageTimeMap).sort((a, b) => (a.statuses[0] && b.statuses[0]
      ? a.statuses[0].lastUpdated - b.statuses[0].lastUpdated : 1))
      .forEach((task = {}) => {
        const { statuses = [] } = task;
        if (!statuses?.length) return;
        const { failed: equipmentFailed, inProgress: equipmentInProgress } = getEquipmentSyncState(
          task.equipment,
          { now, writtenThreshold },
        );
        if (statuses[0]?.resolved
          || (statuses[0]?.status === 'synced' && !equipmentFailed && !equipmentInProgress)) {
          resolved.push(task);
        } else if (
          (statuses[0]?.status === 'written' && (now - statuses[0].lastUpdated < writtenThreshold))
          || statuses[0]?.status === 'writeRetry'
          || (statuses[0]?.status === 'synced' && equipmentInProgress && !equipmentFailed)
        ) {
          inProgress.push(task);
        } else unresolved.push(task);
      });
    return {
      unresolvedTasks: unresolved,
      inProgressTasks: inProgress,
      resolvedTasks: resolved,
    };
  }, [sageTimeMap]);

  useEffect(() => {
    if (visible) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([
          dispatch(getSageData(selectedIntegration?.id, 'sage100')),
          dispatch(getSageTimeSync({ integrationId: selectedIntegration?.id })),
        ]);
        setLoading(false);
      };
      loadData();
    } else {
      setSelectedTaskId();
      setRetryingEquipmentId();
      setLoading(false);
      setSelected([]);
      setActivePanels(DEFAULT_PANELS);
    }
  }, [visible, selectedIntegration]);

  useEffect(() => {
    if (!selectedTaskId) {
      if (unresolvedTasks?.length) setSelectedTaskId(unresolvedTasks[0].id);
      else if (resolvedTasks?.length) setSelectedTaskId(resolvedTasks[0].id);
    }
  }, [unresolvedTasks, resolvedTasks, selectedTaskId]);

  const postSelected = useCallback(async () => {
    const selectedTasks = new Set(selected);
    const timeRetryIds = [];
    const equipmentRetryIds = [];
    selectedTasks.forEach((taskId) => {
      const task = sageTimeMap[taskId];
      if (!task) return;
      if (!isTimeEntrySynced(task)) {
        timeRetryIds.push(taskId);
        return;
      }
      equipmentRetryIds.push(...getRetryableEquipmentIds(task));
    });
    setLoading(true);
    if (timeRetryIds.length) {
      await dispatch(writeSageTime(selectedIntegration?.id, timeRetryIds));
    }
    if (equipmentRetryIds.length) {
      await dispatch(writeSageEquipment(selectedIntegration?.id, equipmentRetryIds));
    }
    setSelected([]);
    setLoading(false);
  }, [selected, selectedIntegration, sageTimeMap]);

  const resolveSelected = useCallback(async () => {
    const selectedTasks = new Set(selected);
    setLoading(true);
    if (selectedTasks.size) {
      await dispatch(resolveSageTime(selectedIntegration?.id, Array.from(selectedTasks), true));
    }
    setSelected([]);
    setLoading(false);
  }, [selected, selectedIntegration]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await dispatch(getSageTimeSync({ integrationId: selectedIntegration?.id }));
    setLoading(false);
  }, [selectedIntegration]);

  const allTasks = useMemo(() => Object.values(sageTimeMap), [sageTimeMap]);
  const userFilters = useMemo(() => getUniqueFilters(
    allTasks,
    ({ userId } = {}) => userId,
    ({ userId } = {}) => userMap[userId]?.name ?? 'Unknown',
  ), [allTasks, userMap]);
  const dateFilters = useMemo(() => getUniqueFilters(
    allTasks,
    ({ sagePayload: { Day } = {} } = {}) => Day || '-',
    ({ sagePayload: { Day } = {} } = {}) => Day || '-',
  ), [allTasks]);
  const projectFilters = useMemo(() => getUniqueFilters(
    allTasks,
    ({ projectId } = {}) => projectId || '-',
    ({ projectId } = {}) => projectMap[projectId]?.name ?? '-',
  ), [allTasks, projectMap]);
  const costcodeFilters = useMemo(() => getUniqueFilters(
    allTasks,
    ({ costcodeId } = {}) => costcodeId || '-',
    ({ costcodeId } = {}) => costcodeMap[costcodeId]?.name ?? '-',
  ), [allTasks, costcodeMap]);

  const renderUserId = useCallback((userId) => (userMap[userId]?.name ?? 'Unknown'), [userMap]);
  const renderDate = useCallback((_, { sagePayload: { Day } = {} } = {}) => Day, []);
  const renderHours = useCallback((_, { sagePayload: { Value } = {} } = {}) => Value, []);
  const renderProject = useCallback((_, { projectId } = {}) => projectMap[projectId]?.name ?? '-', [projectMap]);
  const renderCostcode = useCallback((_, { costcodeId } = {}) => costcodeMap[costcodeId]?.name ?? '-', [costcodeMap]);
  const renderEquipment = useCallback((_, { equipment } = {}) => (
    formatEquipmentSyncSummary(equipment)
  ), []);

  const retryEquipment = useCallback(async (equipmentEntryIds) => {
    if (!equipmentEntryIds?.length) return;
    setRetryingEquipmentId(equipmentEntryIds[0]);
    setLoading(true);
    await dispatch(writeSageEquipment(selectedIntegration?.id, equipmentEntryIds));
    setLoading(false);
    setRetryingEquipmentId();
  }, [selectedIntegration]);

  const taskColumns = useMemo(() => ([
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      render: renderUserId,
      filters: userFilters,
      onFilter: (userId, record) => record.userId === userId,
      filterDropdown: SearchFormFilterHeader,
      sorter: sortByLookupName('userId', userMap),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }, {
      title: 'Date',
      key: 'date',
      render: renderDate,
      filters: dateFilters,
      onFilter: (day, { sagePayload: { Day } = {} } = {}) => (Day || '-') === day,
      filterDropdown: SearchFormFilterHeader,
      sorter: (a, b) => (a.sagePayload?.Day ?? '').localeCompare(b.sagePayload?.Day ?? ''),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }, {
      title: 'Hours',
      key: 'hours',
      render: renderHours,
      sorter: (a, b) => (
        (parseFloat(a.sagePayload?.Value) || 0) - (parseFloat(b.sagePayload?.Value) || 0)
      ),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }, {
      title: t('Project'),
      key: 'projectId',
      render: renderProject,
      filters: projectFilters,
      onFilter: (projectId, record) => (record.projectId || '-') === projectId,
      filterDropdown: SearchFormFilterHeader,
      sorter: sortByLookupName('projectId', projectMap),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }, {
      title: t('Costcode'),
      key: 'costcodeId',
      render: renderCostcode,
      filters: costcodeFilters,
      onFilter: (costcodeId, record) => (record.costcodeId || '-') === costcodeId,
      filterDropdown: SearchFormFilterHeader,
      sorter: sortByLookupName('costcodeId', costcodeMap),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }, {
      title: t('Equipment'),
      key: 'equipment',
      render: renderEquipment,
      filters: EQUIPMENT_FILTERS,
      onFilter: (value, record) => matchesEquipmentFilter(value, record.equipment),
      filterDropdown: SearchFormFilterHeader,
      sorter: (a, b) => (
        formatEquipmentSyncSummary(a.equipment)
          .localeCompare(formatEquipmentSyncSummary(b.equipment))
      ),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    },
  ]), [
    costcodeFilters,
    costcodeMap,
    dateFilters,
    projectFilters,
    projectMap,
    renderCostcode,
    renderEquipment,
    renderHours,
    renderProject,
    renderUserId,
    t,
    userFilters,
    userMap,
  ]);

  const resolvedEntriesSelected = useMemo(() => {
    const resolvedMap = getIdMap(resolvedTasks);
    return selected.some((id) => resolvedMap[id]);
  }, [selected, resolvedTasks]);

  const rowClassName = useCallback((record) => ((record?.id === selectedTask?.id) ? 'selected-row' : 'ontraccr-row'), [selectedTask]);
  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [settings]);

  const rowSelection = useMemo(() => ({
    type: 'checkbox',
    onChange: (newSelected) => setSelected(newSelected),
    selectedRowKeys: selected,
    hideSelectAll: true,
    getCheckboxProps: () => ({
      className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
    }),
  }), [selected, settings]);
  const onRow = useCallback((entry) => ({
    onClick: () => setSelectedTaskId(entry.id),
  }), []);

  return (
    <Drawer
      title="Sage 100 Time Entry Syncing"
      visible={visible}
      width="90vw"
      onClose={onClose}
      style={{
        height: '100%',
      }}
    >
      <div
        style={{
          height: '100%',
        }}
      >
        <Row
          gutter={[16, 16]}
          style={{
            height: '100%',
          }}
        >
          <Col
            span={13}
            style={{
              height: '100%',
            }}
          >
            <Row>
              <Col
                flex={1}
                style={{
                  borderBottom: '1px solid #e9e9e9',
                  background: '#fff',
                  paddingBottom: 10,
                }}
              >
                <OnTraccrButton
                  title="Refresh"
                  style={{ marginRight: 45 }}
                  icon={<ReloadOutlined />}
                  disabled={loading}
                  onClick={refreshData}
                />
                <OnTraccrButton
                  title="Retry Selected"
                  style={{ marginRight: 45 }}
                  disabled={loading || !selected?.length}
                  onClick={postSelected}
                />
                <OnTraccrButton
                  title="Resolve Selected"
                  style={{ marginRight: 45 }}
                  disabled={loading || !selected?.length || resolvedEntriesSelected}
                  onClick={resolveSelected}
                />
              </Col>
            </Row>
            <Row
              style={{
                height: '100%',
              }}
            >
              <Col
                flex={1}
                style={{
                  height: '100%',
                  overflowY: 'scroll',
                  paddingTop: 10,
                  paddingRight: 20,
                  paddingBottom: 10,
                }}
              >
                <Collapse
                  ghost
                  activeKey={activePanels}
                  onChange={setActivePanels}
                >
                  <Panel
                    header={`Errors / Unresolved (${unresolvedTasks.length})`}
                    key="unresolved"
                    className="ontraccr-collapse-panel"
                  >
                    <Table
                      loading={loading}
                      rowKey="id"
                      rowSelection={rowSelection}
                      onRow={onRow}
                      rowClassName={rowClassName}
                      columns={taskColumns}
                      bordered
                      onHeaderRow={onHeaderRow}
                      pagination={false}
                      scroll={TASK_TABLE_SCROLL}
                      dataSource={unresolvedTasks}
                      locale={{ emptyText: 'No unresolved errors' }}
                    />
                  </Panel>
                  <Panel
                    header={`In Progress (${inProgressTasks.length})`}
                    key="inProgress"
                    className="ontraccr-collapse-panel"
                  >
                    <Table
                      loading={loading}
                      rowKey="id"
                      rowSelection={rowSelection}
                      onRow={onRow}
                      rowClassName={rowClassName}
                      columns={taskColumns}
                      bordered
                      onHeaderRow={onHeaderRow}
                      pagination={false}
                      scroll={TASK_TABLE_SCROLL}
                      dataSource={inProgressTasks}
                      locale={{ emptyText: 'No syncs in progress' }}
                    />
                  </Panel>
                  <Panel
                    header={`Successful / Resolved (${resolvedTasks.length})`}
                    key="resolved"
                    className="ontraccr-collapse-panel"
                  >
                    <Table
                      loading={loading}
                      rowKey="id"
                      rowSelection={rowSelection}
                      onRow={onRow}
                      rowClassName={rowClassName}
                      columns={taskColumns}
                      bordered
                      onHeaderRow={onHeaderRow}
                      scroll={TASK_TABLE_SCROLL}
                      pagination={{
                        hideOnSinglePage: true,
                        pageSize: 20,
                        showSizeChanger: true,
                      }}
                      dataSource={resolvedTasks}
                    />
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </Col>
          <Col
            span={11}
            style={{
              overflowY: 'scroll',
              height: '100%',
              paddingTop: 10,
            }}
          >
            <SageTimeSyncTaskDetails
              loading={loading}
              selectedTask={selectedTask}
              onRetryEquipment={retryEquipment}
              retryingEquipmentId={retryingEquipmentId}
            />
          </Col>
        </Row>
      </div>
    </Drawer>
  );
}

SageTimeSyncDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  selectedIntegration: PropTypes.shape({
    id: PropTypes.string,
  }),
  visible: PropTypes.bool,
};

SageTimeSyncDrawer.defaultProps = {
  selectedIntegration: {},
  visible: false,
};
